import { localText } from "@serenity-is/corelib";

/**
 * On a wide screen the menu button in the page header folds the sidebar to a strip of icons and
 * back. Pointing at the strip, or tabbing into it, opens it over the page until the pointer leaves.
 * The choice is kept in the SidebarPaneCollapsed cookie, which _Layout.cshtml reads, so the next
 * page starts folded without a flicker. On a narrow screen the button keeps Serenity's slide-in menu.
 */
export function initSidebarToggle() {
    if (typeof document === "undefined" || (document as any).sidebarToggleReady)
        return;
    (document as any).sidebarToggleReady = true;

    if (document.body)
        start();
    else
        document.addEventListener("DOMContentLoaded", start, { once: true });
}

const foldedClass = "s-sidebar-pane-collapsed";
const peekClass = "s-sidebar-peek";
const wideScreen = window.matchMedia("(min-width: 1200px)");

const text = (key: string, fallback: string) => localText("Site.Sidebar." + key, fallback);
const folded = () => document.body.classList.contains(foldedClass);

function start() {
    const sidebar = document.getElementById("s-sidebar");
    const button = document.getElementById("s-sidebar-toggler");
    if (!sidebar || !button)
        return;

    const render = () => {
        if (!wideScreen.matches) {
            button.removeAttribute("title");
            return;
        }
        button.title = folded() ? text("Expand", "Expand menu") : text("Collapse", "Collapse menu");
        button.setAttribute("aria-expanded", folded() ? "false" : "true");
    };

    const peek = (open: boolean) => sidebar.classList.toggle(peekClass, open && folded() && wideScreen.matches);

    const fold = (value: boolean) => {
        document.body.classList.toggle(foldedClass, value);
        peek(false);
        document.cookie = "SidebarPaneCollapsed=" + (value ? "1;max-age=31536000" : ";max-age=0") + ";path=/;samesite=lax";
        render();
        // the page beside the sidebar changes width once the strip has moved (0.2s in site.css);
        // grids and charts size themselves on resize
        window.setTimeout(() => window.dispatchEvent(new Event("resize")), 250);
    };

    // runs before Serenity's own handler on the button, which opens the narrow-screen menu
    document.addEventListener("click", e => {
        if (!wideScreen.matches || !(e.target as Element)?.closest?.("#s-sidebar-toggler"))
            return;
        e.stopPropagation();
        fold(!folded());
    }, true);

    // a short wait, so a pointer passing over the strip on its way elsewhere doesn't open it
    let timer: number;
    sidebar.addEventListener("mouseenter", () => {
        clearTimeout(timer);
        timer = window.setTimeout(() => peek(true), 150);
    });
    sidebar.addEventListener("mouseleave", () => {
        clearTimeout(timer);
        // stays open while the person types in the search box or moves through it by keyboard
        if (!sidebar.querySelector(":focus-visible"))
            peek(false);
    });
    sidebar.addEventListener("focusin", () => {
        if (sidebar.querySelector(":focus-visible"))
            peek(true);
    });
    sidebar.addEventListener("focusout", e => {
        if (!sidebar.contains(e.relatedTarget as Node) && !sidebar.matches(":hover"))
            peek(false);
    });
    sidebar.addEventListener("keydown", e => {
        if (e.key === "Escape" && sidebar.classList.contains(peekClass)) {
            peek(false);
            button.focus();
        }
    });

    wideScreen.addEventListener("change", () => {
        peek(false);
        render();
    });

    render();
}
